"use client"

import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { useIsDesktop } from "ui"
import Container from "ui/Container"
import { mediaQueries } from "ui/theme/mediaQueries"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"
import { extractProperty } from "utils"
import TabItem from "./TabItem"

export const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "$inview",
})<{ $inview: boolean }>`
  opacity: ${(p) => (p.$inview ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inview ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
`

const TabsContainer = styled.nav``

export const StyledTab = styled(Tab)`
  font-weight: 400;
  font-size: 20px;
  line-height: 140%;
  height: auto;
  &.Mui-selected {
    font-weight: 700;
  }
`

const StyledContainer = styled.div`
  width: 100%;
  padding: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 48px 0;
  }
`

const StyledSection = styled.section`
  padding: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0px;
  }
`

const StyledTabsDiv = styled.div`
  position: sticky;
  top: 64px;
  z-index: 10;
  background-color: white;
  width: 100%;
  ${mediaQueries.desktopM} {
    top: 80px;
  }
`

const StyledTabsInner = styled.div<{ $scrolled: boolean }>`
  width: 100%;
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? "0px 12px 24px 0px #26303d29" : "none"};
  padding-top: ${({ $scrolled }) => ($scrolled ? "24px" : "48px")};
  padding-bottom: ${({ $scrolled }) => ($scrolled ? "24px" : "0px")};
  transition: all 0.2s ease-in-out;
`

export type TabItemType = { subTitle: string; content: string; media: string }[]

export default function TabsContent({
  data,
  anchorBlock,
}: {
  data: any
  anchorBlock: any
}) {
  const anchorMenuBlock = extractProperty(anchorBlock, "anchorMenuItems") || []
  const anchorMenuArray = anchorMenuBlock.map((el: any) => {
    return {
      anchorId: extractProperty(el, "anchorId"),
      itemText: extractProperty(el, "itemText"),
    }
  }) as { anchorId: string; itemText: string }[]
  const itemsList = extractProperty(data, "itemsList") || []
  const items = itemsList.map((el: any) => {
    const anchorName = extractProperty(el, "anchorName")
    const content = extractProperty(el, "itemsList") || []
    const contentItems =
      content.map((el: any) => ({
        subTitle: extractProperty(el, "subTitle"),
        content: extractProperty(el, "content"),
        media: extractProperty(el, "media")?.[0]?.url,
      })) || []
    return {
      anchorName,
      contentItems,
    }
  }) as {
    anchorName: string
    contentItems: TabItemType
  }[]

  // Connect items with their corresponding menu labels, but show all items
  const connectedTabs = items.map((item, index) => {
    // Try to find matching menu item by anchorName/anchorId
    const matchingMenuitem = anchorMenuArray.find(
      (menuItem) => menuItem.anchorId === item.anchorName,
    )

    // If we have a matching menu item, use its itemText as label
    if (matchingMenuitem) {
      return {
        anchorId: item.anchorName,
        itemText: matchingMenuitem.itemText,
        contentItems: item.contentItems,
      }
    }

    // For items without matching menu (like empty anchorName),
    // still include them but without a specific label
    return {
      anchorId: item.anchorName || `section-${index}`,
      itemText: "", // No fallback label
      contentItems: item.contentItems,
    }
  }) as {
    anchorId: string
    itemText: string
    contentItems: TabItemType
  }[]

  console.log(anchorMenuArray, items, connectedTabs, "connected tabs data")
  const [value, setValue] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { ref, inView } = useInView({ ...useInViewConfig, threshold: 0 })

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)

    // Get the anchorId of the selected tab from connectedTabs
    const selectedAnchorId = connectedTabs[newValue].anchorId

    // Find the section with the matching ID and scroll to it
    const sectionElement = document.getElementById(selectedAnchorId)
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" })
    }
  }

  const isDesktop = useIsDesktop()

  // Use Intersection Observer instead of scroll events
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel is NOT visible (scrolled past), tabs should appear scrolled
        setScrolled(!entry.isIntersecting)
      },
      {
        threshold: 0,
        rootMargin: isDesktop ? "-80px 0px 0px 0px" : "-64px 0px 0px 0px",
      },
    )

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current)
    }

    return () => observer.disconnect()
  }, [isDesktop])
  return (
    <>
      {/* Sentinel element to detect when tabs should appear scrolled */}
      <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />

      <StyledTabsDiv ref={containerRef}>
        <StyledTabsInner $scrolled={scrolled}>
          <Container paddingMobile={true}>
            <TabsContainer>
              <StyledTabs
                $inview={inView}
                value={value}
                onChange={handleChange}
                aria-label="section navigation"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                role="navigation"
              >
                {connectedTabs.map((tab, index) => (
                  <StyledTab
                    label={tab.itemText}
                    key={index}
                    role="button"
                    aria-label={`Navigate to ${tab.itemText} section`}
                    id={`nav-button-${index}`}
                  />
                ))}
              </StyledTabs>
            </TabsContainer>
          </Container>
        </StyledTabsInner>
      </StyledTabsDiv>
      <Container paddingMobile={true}>
        <StyledContainer ref={ref}>
          {connectedTabs.map((tab, index) => (
            <StyledSection
              id={tab.anchorId}
              key={index}
              aria-labelledby={`nav-button-${index}`}
            >
              <TabItem data={tab.contentItems} />
            </StyledSection>
          ))}
          {/* <CommonTabs data={tabsData} value={value} setValue={setValue} /> */}
        </StyledContainer>
      </Container>
    </>
  )
}
